import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Select,
  MenuItem,
  TextField,
  Stack,
  Chip,
  IconButton,
  Alert,
  FormControl,
  InputLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  Divider,
} from '@mui/material';
import {
  Assignment,
  DriveEta,
  TwoWheeler,
  Star,
  Edit,
  Save,
  Cancel,
  ExpandMore,
  CheckCircle,
  Warning,
  Error,
  Add,
} from '@mui/icons-material';
import { useAPI } from '../../hooks/useAPI';

interface TrackerData {
  trackerId: string;
  currentRole: string;
  title: string;
  status: 'active' | 'standby' | 'offline';
  isDistanceReference: boolean;
  priority: number;
  confidenceScore?: number;
}

interface AssignmentData {
  trackerId: string;
  role: string;
  title: string;
  priority: number;
}

interface AssignmentPanelProps {
  trackers: TrackerData[];
  assignments: AssignmentData[];
}

const AssignmentPanel: React.FC<AssignmentPanelProps> = ({ trackers, assignments }) => {
  const [editingTracker, setEditingTracker] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editRole, setEditRole] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [waitingForUpdate, setWaitingForUpdate] = useState(false);

  // New tracker creation state
  const [showAddTracker, setShowAddTracker] = useState(false);
  const [newTrackerId, setNewTrackerId] = useState('');
  const [newTrackerTitle, setNewTrackerTitle] = useState('');
  const [newTrackerRole, setNewTrackerRole] = useState('');
  const [newTrackerType, setNewTrackerType] = useState('race_director');
  const [newTrackerTelco, setNewTrackerTelco] = useState('telco_a');

  const api = useAPI();

  // Effect to handle WebSocket updates and exit edit mode
  React.useEffect(() => {
    if (waitingForUpdate && editingTracker) {
      const currentTracker = trackers.find(t => t.trackerId === editingTracker);
      if (currentTracker &&
          currentTracker.title === editTitle &&
          currentTracker.currentRole === editRole) {
        console.log('AssignmentPanel: WebSocket update received, exiting edit mode');
        // Data has been updated via WebSocket, exit edit mode
        setEditingTracker(null);
        setLoading(false);
        setWaitingForUpdate(false);
        setEditTitle('');
        setEditRole('');
      }
    }
  }, [trackers, editingTracker, editTitle, editRole, waitingForUpdate]);

  // Predefined roles for quick assignment
  const predefinedRoles = [
    { value: 'race_director_a', label: 'Race Director A (Primary Distance)', icon: <Star />, color: 'red' },
    { value: 'race_director_b', label: 'Race Director B (Backup Distance)', icon: <DriveEta />, color: 'darkred' },
    { value: 'leaders', label: 'Leaders Group', icon: <Star />, color: 'gold' },
    { value: 'peloton', label: 'Main Peloton', icon: <TwoWheeler />, color: 'blue' },
    { value: 'chase_group', label: 'Chase Group', icon: <TwoWheeler />, color: 'orange' },
    { value: 'motor_1', label: 'Motor 1', icon: <TwoWheeler />, color: 'green' },
    { value: 'motor_2', label: 'Motor 2', icon: <TwoWheeler />, color: 'purple' },
    { value: 'standby', label: 'Standby', icon: <Assignment />, color: 'gray' },
  ];


  const handleStartEdit = (tracker: TrackerData) => {
    setEditingTracker(tracker.trackerId);
    setEditTitle(tracker.title);
    setEditRole(tracker.currentRole);
    setMessage(null);
  };

  const handleSaveEdit = async () => {
    if (!editingTracker) return;

    try {
      setLoading(true);
      console.log('AssignmentPanel: Saving assignment', { trackerId: editingTracker, role: editRole, customTitle: editTitle });

      const response = await api.post(`/assignments/${editingTracker}/assign`, {
        role: editRole,
        customTitle: editTitle,
      });

      console.log('AssignmentPanel: Assignment save response:', response.data);
      setMessage({ type: 'success', text: 'Assignment updated successfully - changes will appear shortly' });
      setWaitingForUpdate(true);

      // Keep edit mode active with loading state to show visual feedback
      // WebSocket will update the data and we can exit edit mode when data changes

    } catch (error: any) {
      setMessage({ type: 'error', text: 'Failed to update assignment' });
      console.error('Assignment error:', error);
      setLoading(false);
      setEditingTracker(null);
      setWaitingForUpdate(false);
    }
    // Don't set loading to false here - let WebSocket response handle it
  };

  const handleCancelEdit = () => {
    setEditingTracker(null);
    setEditTitle('');
    setEditRole('');
    setMessage(null);
  };


  const handleCreateTracker = async () => {
    if (!newTrackerId.trim() || !newTrackerTitle.trim() || !newTrackerRole.trim()) {
      setMessage({ type: 'error', text: 'Please fill in all required fields' });
      return;
    }

    try {
      setLoading(true);

      // Create and assign the new tracker in one call
      await api.post(`/assignments/${newTrackerId}/assign`, {
        role: newTrackerRole,
        customTitle: newTrackerTitle,
        deviceType: newTrackerType,
        telco: newTrackerTelco,
        isDistanceReference: newTrackerRole === 'race_director_a',
        priority: newTrackerRole === 'race_director_a' ? 1 : 2
      });

      setMessage({ type: 'success', text: `Tracker ${newTrackerId} created successfully` });

      // Reset form
      setShowAddTracker(false);
      setNewTrackerId('');
      setNewTrackerTitle('');
      setNewTrackerRole('');
      setNewTrackerType('race_director');
      setNewTrackerTelco('telco_a');

      // Refresh page to show new tracker
      setTimeout(() => window.location.reload(), 1000);
    } catch (error: any) {
      setMessage({ type: 'error', text: `Failed to create tracker: ${error.response?.data?.error || error.message}` });
      console.error('Create tracker error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelAdd = () => {
    setShowAddTracker(false);
    setNewTrackerId('');
    setNewTrackerTitle('');
    setNewTrackerRole('');
    setNewTrackerType('race_director');
    setNewTrackerTelco('telco_a');
    setMessage(null);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'standby': return 'warning';
      case 'offline': return 'error';
      default: return 'default';
    }
  };

  const getConfidenceIcon = (score?: number) => {
    if (score === undefined) return <Warning color="warning" />;
    if (score >= 80) return <CheckCircle color="success" />;
    if (score >= 60) return <Warning color="warning" />;
    return <Error color="error" />;
  };

  return (
    <Box>
      {/* Message Display */}
      {message && (
        <Alert
          severity={message.type}
          onClose={() => setMessage(null)}
          sx={{ mb: 2 }}
        >
          {message.text}
        </Alert>
      )}


      {/* Add New Tracker */}
      <Accordion sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="subtitle2">Add New Tracker</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {!showAddTracker ? (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setShowAddTracker(true)}
              disabled={loading}
              fullWidth
            >
              Add New Tracker
            </Button>
          ) : (
            <Stack spacing={2}>
              <TextField
                size="small"
                label="Tracker ID *"
                value={newTrackerId}
                onChange={(e) => setNewTrackerId(e.target.value)}
                placeholder="e.g., 860201065163902"
                fullWidth
                helperText="Device unique ID or IMEI"
              />

              <TextField
                size="small"
                label="Custom Title *"
                value={newTrackerTitle}
                onChange={(e) => setNewTrackerTitle(e.target.value)}
                placeholder="e.g., Recce Car 1"
                fullWidth
              />

              <FormControl size="small" fullWidth>
                <InputLabel>Role *</InputLabel>
                <Select
                  value={newTrackerRole}
                  onChange={(e) => setNewTrackerRole(e.target.value)}
                  label="Role *"
                >
                  {predefinedRoles.map((role) => (
                    <MenuItem key={role.value} value={role.value}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        {role.icon}
                        <Typography>{role.label}</Typography>
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" fullWidth>
                <InputLabel>Device Type</InputLabel>
                <Select
                  value={newTrackerType}
                  onChange={(e) => setNewTrackerType(e.target.value)}
                  label="Device Type"
                >
                  <MenuItem value="race_director">Race Director Car</MenuItem>
                  <MenuItem value="motor">Motor Bike</MenuItem>
                </Select>
              </FormControl>

              <FormControl size="small" fullWidth>
                <InputLabel>Telco</InputLabel>
                <Select
                  value={newTrackerTelco}
                  onChange={(e) => setNewTrackerTelco(e.target.value)}
                  label="Telco"
                >
                  <MenuItem value="telco_a">Telco A</MenuItem>
                  <MenuItem value="telco_b">Telco B</MenuItem>
                </Select>
              </FormControl>

              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  startIcon={<Save />}
                  onClick={handleCreateTracker}
                  disabled={loading}
                  fullWidth
                >
                  Create Tracker
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Cancel />}
                  onClick={handleCancelAdd}
                  disabled={loading}
                  fullWidth
                >
                  Cancel
                </Button>
              </Stack>
            </Stack>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Tracker List */}
      <List dense>
        {trackers.map((tracker) => (
          <React.Fragment key={tracker.trackerId}>
            <ListItem
              sx={{
                bgcolor: tracker.isDistanceReference ? 'action.selected' : 'transparent',
                borderRadius: 1,
                mb: 1,
                border: tracker.isDistanceReference ? '2px solid' : '1px solid',
                borderColor: tracker.isDistanceReference ? 'primary.main' : 'divider',
              }}
            >
              <ListItemIcon>
                {tracker.isDistanceReference && <Star color="primary" />}
                {getConfidenceIcon(tracker.confidenceScore)}
              </ListItemIcon>

              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                  <Typography variant="subtitle2">
                    {tracker.trackerId}
                  </Typography>
                  <Chip
                    label={tracker.status}
                    size="small"
                    color={getStatusColor(tracker.status) as any}
                  />
                  {tracker.isDistanceReference && (
                    <Chip
                      label="PRIMARY"
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  )}
                </Stack>

                {editingTracker === tracker.trackerId ? (
                  <Stack spacing={1}>
                    <FormControl size="small" fullWidth>
                      <InputLabel>Role</InputLabel>
                      <Select
                        value={editRole}
                        onChange={(e) => setEditRole(e.target.value)}
                        label="Role"
                      >
                        {predefinedRoles.map((role) => (
                          <MenuItem key={role.value} value={role.value}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              {role.icon}
                              <Typography component="span">{role.label}</Typography>
                            </Stack>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <TextField
                      size="small"
                      label="Custom Title"
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                      fullWidth
                    />

                    <Stack direction="row" spacing={1}>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<Save />}
                        onClick={handleSaveEdit}
                        disabled={loading}
                      >
                        Save
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Cancel />}
                        onClick={handleCancelEdit}
                        disabled={loading}
                      >
                        Cancel
                      </Button>
                    </Stack>
                  </Stack>
                ) : (
                  <Stack spacing={0.5}>
                    <Typography variant="body2" color="primary" component="div">
                      {tracker.title || tracker.currentRole}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" component="div">
                      Role: {tracker.currentRole} | Priority: {tracker.priority}
                    </Typography>
                    {tracker.confidenceScore !== undefined && (
                      <Typography variant="caption" color="text.secondary" component="div">
                        Confidence: {tracker.confidenceScore.toFixed(1)}%
                      </Typography>
                    )}
                  </Stack>
                )}
              </Box>

              {editingTracker !== tracker.trackerId && (
                <IconButton
                  size="small"
                  onClick={() => handleStartEdit(tracker)}
                  disabled={loading}
                >
                  <Edit />
                </IconButton>
              )}
            </ListItem>
            <Divider />
          </React.Fragment>
        ))}
      </List>

      {trackers.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body2" color="text.secondary">
            No trackers available
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default AssignmentPanel;